// The running-out sheet, opened in place by a server 409
// `{ insufficient_credits: { balance, cost, action } }`:
//
//   Not enough credits for this
//   This needs 600 credits; you have 350.
//   Buy credits            1,500 · $45  covers this  [Buy]  …
//   Change plan ▸          (rows on request)
//
// Two plain options, no comparison table until asked for, and the listing
// underneath is never lost: the sheet stashes where it opened — and what was
// about to happen — so the Stripe legs (/success, /subscription-success,
// /cancel) come straight back and offer the action again. The pack rows are
// TopUpSheet's, so a top-up looks the same wherever it starts.

import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { API_URL } from "../../constants";
import {
  PLANS,
  PLAN_KEYS,
  PLAN_LABELS,
  formatCredits,
  formatDollars,
  stashCreditsReturn,
} from "../../constants/credits";
import { track } from "../../utils/analytics";
import { authHeader } from "../../utils/auth";
import { storageGet } from "../../utils/storage";
import CostLine from "./CostLine";
import Sheet, { SheetActions, SheetError, SheetSecondary, SheetTitle } from "./Sheet";
import { TopUpPacks, checkoutErrorText } from "./TopUpSheet";

const Section = styled.div`
  margin-top: 18px;
`;
const SectionLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--brand-ink-4, #8a9a9d);
  margin-bottom: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-top: 1px solid var(--brand-border, #e3ebec);

  &:last-child {
    border-bottom: 1px solid var(--brand-border, #e3ebec);
  }
`;

const RowText = styled.div`
  font-size: 14px;
  color: var(--brand-ink, #0b1c1e);
  line-height: 1.4;

  span {
    color: var(--brand-ink-3, #5c7074);
  }
`;

const RowButton = styled.button`
  background: none;
  border: 1px solid var(--brand-primary, #0e7e7a);
  color: var(--brand-primary-700, #085552);
  border-radius: 999px;
  padding: 7px 16px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: var(--brand-primary-50, #e6f3f2);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ExpandLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  color: var(--brand-primary-700, #085552);
  cursor: pointer;
  text-decoration: underline;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

/**
 * @param {{
 *   block: { balance: number, cost: number, action: string },
 *   onClose: () => void,
 *   returnFocusRef?: React.RefObject<HTMLElement>,
 *   returnPath?: string,
 *   listingUuid?: string,
 *   resume?: import("../../constants/credits").CreditsResume,
 * }} props
 *   block: the server's 409 payload (see insufficientCreditsFrom).
 *   returnPath: where the Stripe legs should come back to; defaults to the
 *   current location. listingUuid rides the stash so the return can re-seed
 *   the listing; resume is the blocked action, offered again on return.
 */
const CreditsPaywallSheet = ({
  block,
  onClose,
  returnFocusRef,
  returnPath,
  listingUuid,
  resume,
}) => {
  const location = useLocation();
  const [plansOpen, setPlansOpen] = useState(false);
  const [busyKey, setBusyKey] = useState(/** @type {string | null} */ (null));
  const [packsBusy, setPacksBusy] = useState(false);
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const openedRef = useRef(false);
  const checkoutLock = useRef(false);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const close = useCallback(() => {
    if (!checkoutLock.current) onClose();
  }, [onClose]);

  const returnStash = useMemo(
    () => ({
      path: returnPath || `${location.pathname}${location.search}`,
      ...(listingUuid ? { listingUuid } : {}),
      ...(resume ? { resume } : {}),
    }),
    [returnPath, listingUuid, resume, location.pathname, location.search]
  );

  // On open: remember where we are, and record the block — once.
  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    stashCreditsReturn(returnStash);
    track("credits_insufficient", {
      action: block.action,
      cost: block.cost,
      balance: block.balance,
    });
  }, [block, returnStash]);

  const busy = busyKey !== null || packsBusy;

  const choosePlan = useCallback(
    /** @param {string} planKey */ async (planKey) => {
      const plan = PLANS[planKey];
      if (!plan || !mountedRef.current || checkoutLock.current) return;
      checkoutLock.current = true;
      setBusyKey(planKey);
      setError(null);
      // Coupons discount purchases now, not debits: a code that arrived via URL
      // (?coupon=) rides the plan checkout here.
      const pendingCoupon = (storageGet("pendingCouponCode") || "").trim();
      try {
        const resp = await axios.post(
          `${API_URL}/subscribe/`,
          {
            plan: plan.product,
            interval: plan.interval,
            ...(pendingCoupon ? { coupon_code: pendingCoupon } : {}),
          },
          { headers: authHeader(), timeout: 30000 }
        );
        if (!mountedRef.current) return;
        if (resp.data?.stripe_url) {
          window.location.href = resp.data.stripe_url;
          return;
        }
        setError("We couldn't start checkout. Please try again in a moment.");
      } catch (err) {
        if (!mountedRef.current) return;
        setError(
          checkoutErrorText(err, "Plans are unavailable right now. Please try again later.")
        );
      }
      checkoutLock.current = false;
      setBusyKey(null);
    },
    []
  );

  const openPlans = () => {
    if (checkoutLock.current) return;
    setPlansOpen(true);
    track("credits_exhausted_view", { action: block.action });
  };

  return (
    <Sheet
      labelledBy="credits-paywall-title"
      onClose={busy ? undefined : close}
      returnFocusRef={returnFocusRef}
    >
      <SheetTitle id="credits-paywall-title">Not enough credits for this</SheetTitle>
      <CostLine cost={block.cost} balance={block.balance} blocked />

      <Section>
        <SectionLabel>Buy credits</SectionLabel>
        <TopUpPacks
          need={Math.max(0, block.cost - block.balance)}
          returnStash={returnStash}
          onBusyChange={setPacksBusy}
          checkoutLock={checkoutLock}
          disabled={busy}
        />
      </Section>

      <Section>
        {plansOpen ? (
          <>
            <SectionLabel>Change plan</SectionLabel>
            {PLAN_KEYS.map((planKey) => {
              const plan = PLANS[planKey];
              const label = PLAN_LABELS[/** @type {keyof typeof PLAN_LABELS} */ (plan.product)];
              const per = plan.interval === "year" ? "a year" : "a month";
              return (
                <Row key={planKey}>
                  <RowText>
                    {label}{" "}
                    <span>
                      · {formatCredits(plan.credits)} {per}
                    </span>{" "}
                    <span>
                      · {formatDollars(plan.amountCents)}/{plan.interval}
                    </span>
                  </RowText>
                  <RowButton
                    type="button"
                    onClick={() => choosePlan(planKey)}
                    disabled={busy}
                    aria-label={`Choose ${label} ${plan.interval === "year" ? "yearly" : "monthly"}`}
                  >
                    {busyKey === planKey ? "Opening…" : "Choose"}
                  </RowButton>
                </Row>
              );
            })}
          </>
        ) : (
          <ExpandLink type="button" onClick={openPlans} disabled={busy}>
            Change plan
          </ExpandLink>
        )}
      </Section>

      {error && <SheetError role="alert">{error}</SheetError>}

      <SheetActions>
        <SheetSecondary type="button" onClick={close} disabled={busy}>
          Not now
        </SheetSecondary>
      </SheetActions>
    </Sheet>
  );
};

export default CreditsPaywallSheet;
